/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import { cloneDeep, flow, groupBy, isEmpty, orderBy, reduce } from 'lodash';
import moment from 'moment';

import {
  RECEIVED_IRT_MESSAGES,
  HAS_SEEN_IRT_MESSAGE
} from './IRTMessagesActions';

import { isBetweenFromAndToDates } from 'services/Dates';

import { promoLinkBuilder, withoutTrailingSlash } from 'services/Externals';

const INTERCEPT_MESSAGE_LOCATION = 5;
const CALL_TO_ACTION_LOCATION = 7;
const CALL_TO_ACTION_PRIORITY = 99;
export const LINK_REGEX = /<link>(.*?)<\/link>/g;
/* For SPLIT_REGEX to do what we expect, the requirment is that <link> content is in the following format:
 Link Text: https://linkurl */
const SPLIT_REGEX = /([^:]*):*(.*)?/;
const NEW_LINE_REGEX = /[\r\n]+/;
const PARSE_MESSAGE_REGEX = /(.*)<Br>\W+<link>([^:]*):(.*)<\/link>/;

export const irtMessageTypes = {
  PRIORITY_INTERCEPT_MESSAGE: 1,
  INTERCEPT_MESSAGE: 2,
  CALL_TO_ACTION: 3,
  PROMO: 4,
  CONTENT: 'CONTENT'
};

const callToActionFn = ({ messagePriority, suppressed, messageLocationId }) =>
  messagePriority == CALL_TO_ACTION_PRIORITY &&
  messageLocationId == CALL_TO_ACTION_LOCATION &&
  !suppressed;
const messageInterceptFn = ({
  // eslint-disable-next-line no-unused-vars
  messagePriority,
  suppressed,
  messageLocationId
}) => messageLocationId == INTERCEPT_MESSAGE_LOCATION && !suppressed;

export const transformPromo = (
  imageMax = '',
  messageId = '',
  messageText = '',
  messageTypeId = '',
  messageName
) => ({
  imageMax: imageMax,
  messageText: promoLinkBuilder(messageText, messageId),
  messageId,
  messageTypeId,
  messageName
});

const transformContent = flow(
  (i) =>
    i.filter(
      (a) => !a.suppressed && a.messagePriority !== CALL_TO_ACTION_PRIORITY
    ),
  (i) => i.slice(0, 3),
  (i) =>
    i.map((i) => {
      const [, messageText, linkText, messageUrl] = PARSE_MESSAGE_REGEX.test(
        i.messageText
      )
        ? i.messageText.match(PARSE_MESSAGE_REGEX)
        : [null, i.messageText, null, null];

      return {
        ...i,
        messageText,
        linkText,
        messageUrl
      };
    })
);

export const transformMessageText = (messageText = '', messageId) => {
  let result;
  let replaceArray = [];
  messageText = messageText.replace(NEW_LINE_REGEX, '<br>');
  messageText = messageText.replace('<br>', '<br><br>');

  while ((result = LINK_REGEX.exec(messageText)) !== null) {
    let linkElementText, linkElementUrl;
    const [linkElement, linkElementContent] = result;
    const [, text, url] = linkElementContent.match(SPLIT_REGEX);

    if (!isEmpty(text)) {
      linkElementText = text.trim();
    }

    if (!isEmpty(url)) {
      const transformedUrl = withoutTrailingSlash(url.trim());
      if (transformedUrl) {
        linkElementUrl = messageId
          ? transformedUrl.indexOf('?') !== -1
            ? transformedUrl + `&messID=${messageId}`
            : transformedUrl + `?messID=${messageId}`
          : transformedUrl;
      }
    }

    replaceArray.push({
      linkElement,
      linkElementUrl,
      linkElementText
    });
  }

  replaceArray.forEach(({ linkElement, linkElementText, linkElementUrl }) => {
    const anchor =
      linkElementUrl && linkElementText
        ? `<a target="_blank"
            href="${linkElementUrl}">${linkElementText}</a>`
        : '';
    messageText = messageText.replace(linkElement, anchor);
  });

  return messageText;
};

const transformCallToAction = (messages) => {
  const {
    messageId = '',
    messageName,
    messageText,
    messageTypeId
  } = messages
    .filter((message) => callToActionFn(message))
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0] || {};

  if (messageText) {
    return {
      messageId,
      messageName,
      messageText: transformMessageText(messageText, messageId),
      messageTypeId
    };
  }

  return {};
};

const transformInterceptMessageAction = flow(
  (messages) => messages.filter((message) => messageInterceptFn(message)),
  (nonSuppressedMessages) =>
    nonSuppressedMessages.filter((message) =>
      isBetweenFromAndToDates(
        moment.utc(),
        moment.utc(message.startDate),
        moment.utc(message.endDate)
      )
    ),
  (filteredMessages) =>
    filteredMessages.map((message) => {
      const {
        imageMax,
        messageId = '',
        messageSummary,
        messageName,
        messageTypeId,
        messageText,
        viewed
      } = message;
      if (messageText) {
        return {
          imageUrl: imageMax,
          messageId,
          messageSummary,
          messageName,
          messageText: transformMessageText(messageText, messageId),
          messageTypeId,
          viewed
        };
      }
      return {};
    })
);

//TODO: Refactor to use Selectors
export const IRTMessagesReducer = (state = {}, { type, messaging }) => {
  if (type === RECEIVED_IRT_MESSAGES) {
    // order by priority, then group by type
    const messageGroups = groupBy(
      orderBy(messaging, ['messagePriority'], ['desc']),
      'messageTypeId'
    );
    return reduce(
      messageGroups,
      (s, value, key) => {
        const { imageMax, messageId, messageText, messageTypeId, messageName } =
          value[0];
        switch (Number(key)) {
          case irtMessageTypes.PROMO:
            return {
              ...s,
              [key]: transformPromo(
                imageMax,
                messageId,
                messageText,
                messageTypeId,
                messageName
              )
            };
          case irtMessageTypes.CALL_TO_ACTION:
            return {
              ...s,
              [key]: transformCallToAction(value),
              [irtMessageTypes.CONTENT]: transformContent(value)
            };
          case irtMessageTypes.INTERCEPT_MESSAGE:
          case irtMessageTypes.PRIORITY_INTERCEPT_MESSAGE:
            return { ...s, [key]: transformInterceptMessageAction(value) };
          default:
            return { ...s, [key]: value };
        }
      },
      {}
    );
  }

  if (type === HAS_SEEN_IRT_MESSAGE) {
    const irtMessagesCopy = cloneDeep(state);
    if (
      messaging.messageTypeId === irtMessageTypes.PRIORITY_INTERCEPT_MESSAGE
    ) {
      irtMessagesCopy[irtMessageTypes.PRIORITY_INTERCEPT_MESSAGE].forEach(
        (message) =>
          message.messageId === messaging.messageId
            ? (message.viewed = true)
            : (message.viewed = false)
      );
      return irtMessagesCopy;
    }

    irtMessagesCopy[irtMessageTypes.INTERCEPT_MESSAGE].forEach((message) =>
      message.messageId === messaging.messageId
        ? (message.viewed = true)
        : (message.viewed = false)
    );
    return irtMessagesCopy;
  }
  return state;
};
